import { supabase } from '../lib/supabase';
import { uploadOfferImage as helperUploadOfferImage, uploadOfferBarcode as helperUploadOfferBarcode, updateOfferImageUrls, checkPartnerAuth, diagnoseStorageIssue } from './partnerStorageHelper';

export interface CreateOfferData {
  title: string;
  description: string;
  category: string;
  offerType?: 'standard' | 'gastronomy'; // NEW: Explicit offer type for mobile app categorization
  partnerId?: string | null;
  address?: string | null;
  regularPrice?: number;
  discountType?: 'percentage' | 'amount' | 'free_goodie' | 'one_plus_one';
  discountPercentage?: number;
  discountAmount?: number;
  freeGoodieDescription?: string;
  redemptionMode?: string;
  customRedemptionCode?: string;
  redemptionUrl?: string; // FIXED: Add redemptionUrl field for URL redemption mode
  redemptionType?: string;
  maxRedemptions?: number;
  redemptionTimeframeDays?: number;
  userRedemptionLimit?: number | null;
  userRedemptionResetDays?: number | null;
  operatingHours?: string;
  imageUrl?: string;
  businessLogoUrl?: string;
  barcodeUrl?: string;
  expiresAt?: string;
  status?: string;
  latitude?: number;
  longitude?: number;
  businessName?: string;
  isFeatured?: boolean; // NEW: Featured offer flag (admin-only)
}

export interface Offer {
  id: string;
  offerId: string;  // New auto-generated offer ID
  offerType?: 'restaurant' | 'attraction'; // NEW: Offer type
  title: string;
  description: string;
  category: string;
  partnerId: string;
  address: string;
  latitude?: number;
  longitude?: number;
  regularPrice?: number;
  moneySaved?: number;
  discountType: string;
  discountPercentage?: number;
  discountAmount?: number;
  freeGoodieDescription?: string;
  redemptionMode: string;
  customRedemptionCode?: string;
  redemptionType: string;
  maxRedemptions?: number;
  redemptionTimeframeDays?: number;
  operatingHours?: string;
  imageUrl?: string;
  businessLogoUrl?: string;
  barcodeUrl?: string;
  qrCode?: string;
  expiresAt?: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  isFeatured?: boolean; // NEW: Featured offer flag
}

export class OfferService {
  // Generate unique QR code for offers with collision detection (duplicates now allowed)
  private async generateUniqueQRCode(): Promise<string> {
    // Since duplicates are now allowed, we can generate simpler codes
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    let qrCode = `OFFER_${timestamp}_${random}`.toUpperCase();
    return qrCode;
  }

  // Get coordinates from address using geocoding
  private async getCoordinatesFromAddress(address: string): Promise<{ latitude: number; longitude: number } | null> {
    try {
      // Check if it's a Vienna address
      const isViennaAddress = address.toLowerCase().includes('wien') || 
                             address.toLowerCase().includes('vienna') ||
                             /\b1[0-2]\d{2}\b/.test(address); // Vienna postal codes

      if (!isViennaAddress) {
        console.warn('Address is not in Vienna, using default coordinates');
      }

      // Try to use Google Geocoding API if available
      const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      
      if (googleMapsApiKey && googleMapsApiKey !== 'your-google-maps-api-key-here') {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleMapsApiKey}`
        );
        
        if (response.ok) {
          const data = await response.json();
          if (data.results && data.results.length > 0) {
            const location = data.results[0].geometry.location;
            return { latitude: location.lat, longitude: location.lng };
          }
        }
      }

      // Fallback to Vienna coordinates with slight randomization for different addresses
      const baseLatitude = 48.2082;
      const baseLongitude = 16.3738;
      
      // Generate consistent coordinates based on address hash
      const addressHash = address.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      
      const latOffset = (addressHash % 200 - 100) / 10000; // ±0.01 degree variation
      const lngOffset = ((addressHash * 31) % 200 - 100) / 10000;
      
      return {
        latitude: baseLatitude + latOffset,
        longitude: baseLongitude + lngOffset
      };
    } catch (error) {
      console.error('Geocoding failed:', error);
      // Return Vienna center as ultimate fallback
      return { latitude: 48.2082, longitude: 16.3738 };
    }
  }

  async createOffer(offerData: CreateOfferData): Promise<Offer> {
    try {
      // CRITICAL FIX: Use database function for partners using localStorage auth
      // This bypasses RLS policies and validates partner internally
      
      // Check if this is a partner creating an offer (has partnerId)
      if (offerData.partnerId) {
        // Use the new database function that bypasses RLS
        return await this.createPartnerOfferViaFunction(offerData);
      }

      // For admin users with Supabase auth, use the original INSERT method
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // CRITICAL FIX: Get the default partner ID if no partner is specified
      let effectivePartnerId = offerData.partnerId;
      if (!effectivePartnerId) {
        const { data: partners, error: partnerError } = await supabase
          .from('partners')
          .select('id')
          .eq('status', 'active')
          .limit(1)
          .single();
        
        if (!partnerError && partners) {
          effectivePartnerId = partners.id;
        }
      }

      // Get coordinates from address if provided
      const coordinates = offerData.address ? 
        await this.getCoordinatesFromAddress(offerData.address) : null;
      
      // CRITICAL FIX: Generate QR code for ALL redemption modes to satisfy NOT NULL constraint
      let qrCode: string | null = null;
      
      if (offerData.redemptionMode === 'custom_code' && offerData.customRedemptionCode) {
        qrCode = offerData.customRedemptionCode;
      } else if (offerData.redemptionMode === 'url' && offerData.redemptionUrl) {
        // FIXED: When redemption mode is URL, use the URL as qr_code to satisfy NOT NULL constraint
        qrCode = offerData.redemptionUrl;
      } else if (offerData.redemptionMode === 'barcode_upload' || offerData.redemptionMode === 'qr_upload') {
        qrCode = await this.generateUniqueQRCode();
      } else {
        // FIXED: For auto_generate and any other mode, generate a unique code
        qrCode = await this.generateUniqueQRCode();
      }

      // FIXED: Map offerType to database offer_type value
      // 'restaurant' | 'gastronomy' → 'gastronomy'
      // 'standard' | 'attraction' → 'standard'
      const dbOfferType = offerData.offerType === 'restaurant' || offerData.offerType === 'gastronomy' ? 'gastronomy' : 'standard';
      
      // NEW: Determine standard_category based on offerType (for backwards compatibility)
      const standardCategory = dbOfferType === 'gastronomy' ? 'Restaurants & Food' : 'Attractions';
      
      const { data, error } = await supabase
        .from('offers')
        .insert({
          title: offerData.title,
          description: offerData.description,
          category: offerData.category,
          offer_type: dbOfferType, // NEW: Set the dedicated offer_type column
          standard_category: standardCategory, // Keep for backwards compatibility
          partner_id: effectivePartnerId || null,
          address: offerData.address || null,
          regular_price: offerData.regularPrice || null,
          discount_type: offerData.discountType || 'percentage',
          discount_percentage: offerData.discountPercentage || null,
          discount_amount: offerData.discountAmount || null,
          free_goodie_description: offerData.freeGoodieDescription || null,
          redemption_mode: offerData.redemptionMode || 'qr_upload',
          custom_redemption_code: offerData.redemptionMode === 'custom_code' ? offerData.customRedemptionCode : (offerData.redemptionMode === 'url' ? offerData.redemptionUrl : null), // FIXED: Store URL in custom_redemption_code when redemption_mode is 'url'
          redemption_url: offerData.redemptionMode === 'url' ? offerData.redemptionUrl : null, // FIXED: Add redemption_url field when redemption_mode is 'url'
          redemption_type: offerData.redemptionType || 'unlimited',
          max_redemptions: offerData.maxRedemptions || null,
          redemption_timeframe_days: offerData.redemptionTimeframeDays || null,
          user_redemption_limit: offerData.userRedemptionLimit || null,
          user_redemption_reset_days: offerData.userRedemptionResetDays || null,
          operating_hours: offerData.operatingHours || null,
          image_url: offerData.imageUrl || null,
          business_logo_url: offerData.businessLogoUrl || null,
          barcode_url: (offerData.redemptionMode === 'barcode_upload' || offerData.redemptionMode === 'qr_upload') ? offerData.barcodeUrl : null,
          qr_code: qrCode,
          created_by: user.id,
          is_featured: offerData.isFeatured || false, // NEW: Include featured status (snake_case for DB)
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating offer:', error);
      throw error;
    }
  }

  /**
   * CRITICAL FIX: Create partner offer using database function
   * This bypasses RLS policies for partners using localStorage auth
   */
  private async createPartnerOfferViaFunction(offerData: CreateOfferData): Promise<Offer> {
    try {
      // Get coordinates from address if provided
      const coordinates = offerData.address ? 
        await this.getCoordinatesFromAddress(offerData.address) : null;
      
      // CRITICAL FIX: Generate QR code for ALL redemption modes to satisfy NOT NULL constraint
      let qrCode: string | null = null;
      
      if (offerData.redemptionMode === 'custom_code' && offerData.customRedemptionCode) {
        qrCode = offerData.customRedemptionCode;
      } else if (offerData.redemptionMode === 'url' && offerData.redemptionUrl) {
        // FIXED: When redemption mode is URL, use the URL as qr_code to satisfy NOT NULL constraint
        qrCode = offerData.redemptionUrl;
      } else if (offerData.redemptionMode === 'barcode_upload' || offerData.redemptionMode === 'qr_upload') {
        qrCode = await this.generateUniqueQRCode();
      } else {
        // FIXED: For auto_generate and any other mode, generate a unique code
        qrCode = await this.generateUniqueQRCode();
      }

      // NEW: Map offerType to database offer_type value
      // 'restaurant' | 'gastronomy' → 'gastronomy'
      // 'standard' | 'attraction' → 'standard'
      const dbOfferType = offerData.offerType === 'restaurant'|| offerData.offerType === 'gastronomy' ?'gastronomy' :'standard';

      // CRITICAL FIX: Add debug logging to verify image URLs are being passed
      console.log('=== IMAGE UPLOAD DEBUG ===');
      console.log('Header Image URL from offerData:', offerData.imageUrl);
      console.log('Business Logo URL from offerData:', offerData.businessLogoUrl);
      console.log('Image URLs being passed to database:', {
        p_image_url: offerData.imageUrl || null,
        p_business_logo_url: offerData.businessLogoUrl || null
      });

      // CRITICAL FIX: Ensure image URLs are properly passed even if they're falsy but not null/undefined
      const imageUrl = offerData.imageUrl ? offerData.imageUrl.trim() : null;
      const businessLogoUrl = offerData.businessLogoUrl ? offerData.businessLogoUrl.trim() : null;

      if (!imageUrl && !businessLogoUrl) {
        console.warn('⚠️ WARNING: Both image URLs are empty! Images may not have been uploaded successfully.');
      }

      // Call the database function that bypasses RLS
      const { data, error } = await supabase
        .rpc('create_partner_offer', {
          p_partner_id: offerData.partnerId,
          p_title: offerData.title,
          p_description: offerData.description,
          p_category: offerData.category,
          p_offer_type: dbOfferType, // NEW: Pass offer_type instead of standard_category
          p_address: offerData.address || null,
          p_regular_price: offerData.regularPrice || null,
          p_discount_type: offerData.discountType || 'percentage',
          p_discount_percentage: offerData.discountPercentage || null,
          p_discount_amount: offerData.discountAmount || null,
          p_free_goodie_description: offerData.freeGoodieDescription || null,
          p_redemption_mode: offerData.redemptionMode || 'qr_upload',
          p_custom_redemption_code: offerData.redemptionMode === 'custom_code' ? offerData.customRedemptionCode : (offerData.redemptionMode === 'url' ? offerData.redemptionUrl : null), // FIXED: Pass redemption URL when mode is 'url'
          p_redemption_url: offerData.redemptionMode === 'url' ? offerData.redemptionUrl : null, // FIXED: Add p_redemption_url parameter for URL mode
          p_redemption_type: offerData.redemptionType || 'unlimited',
          p_max_redemptions: offerData.maxRedemptions || null,
          p_user_redemption_limit: offerData.userRedemptionLimit || null,
          p_user_redemption_reset_days: offerData.userRedemptionResetDays || null,
          p_operating_hours: offerData.operatingHours || null,
          p_image_url: imageUrl, // CRITICAL FIX: Use trimmed image URL
          p_business_logo_url: businessLogoUrl, // CRITICAL FIX: Use trimmed logo URL
          p_barcode_url: (offerData.redemptionMode === 'barcode_upload' || offerData.redemptionMode === 'qr_upload') ? offerData.barcodeUrl : null,
          p_qr_code: qrCode,
          p_expires_at: offerData.expiresAt || null,
          p_latitude: coordinates?.latitude || null,
          p_longitude: coordinates?.longitude || null,
          p_business_name: offerData.businessName || 'Local Business'
        })
        .single();

      if (error) {
        console.error('Database function error:', error);
        throw error;
      }

      if (!data.success) {
        throw new Error(data.message || 'Failed to create offer');
      }

      console.log('✅ Offer created successfully with ID:', data.offer_id);
      console.log('=== END IMAGE UPLOAD DEBUG ===');

      // Fetch the created offer to return complete data
      const { data: offer, error: fetchError } = await supabase
        .from('offers')
        .select('*')
        .eq('id', data.offer_id)
        .single();

      if (fetchError) {
        console.warn('Failed to fetch created offer, returning basic data');
        return { id: data.offer_id, message: data.message };
      }

      // CRITICAL FIX: Verify image URLs in fetched offer
      console.log('=== VERIFICATION ===');
      console.log('Image URL in database:', offer.image_url);
      console.log('Business Logo URL in database:', offer.business_logo_url);
      if (!offer.image_url && !offer.business_logo_url) {
        console.error('❌ CRITICAL: Image URLs are NULL in database! Upload may have failed silently.');
      }

      return offer;
    } catch (error) {
      console.error('Error creating partner offer via function:', error);
      throw error;
    }
  }

  async getAllOffers(): Promise<Offer[]> {
    try {
      const { data, error } = await supabase
        .from('offers')
        .select(`
          *,
          partner:partners(
            business_name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // CRITICAL FIX: Regenerate signed URLs for all offers with barcodes
      const offersWithFreshUrls = await Promise.all((data || []).map(async (offer) => {
        let freshBarcodeUrl = offer.barcode_url;
        
        if (offer.barcode_url && (offer.redemption_mode === 'barcode_upload' || offer.redemption_mode === 'qr_upload')) {
          try {
            const urlObj = new URL(offer.barcode_url);
            const pathMatch = urlObj.pathname.match(/\/storage\/v1\/object\/sign\/offer-barcodes\/(.+?)\?/);
            
            if (pathMatch && pathMatch[1]) {
              const storagePath = decodeURIComponent(pathMatch[1]);
              
              const { data: signedData, error: signError } = await supabase.storage
                .from('offer-barcodes')
                .createSignedUrl(storagePath, 3600 * 24 * 365);
              
              if (!signError && signedData) {
                freshBarcodeUrl = signedData.signedUrl;
              }
            }
          } catch (urlError) {
            console.warn('Failed to regenerate signed URL for barcode:', urlError);
          }
        }

        return {
          id: offer.id,
          offerId: offer.offer_id,
          offerType: offer.standard_category === 'Restaurants & Food' ? 'restaurant' : 'attraction', // NEW: Derive offer type
          title: offer.title,
          description: offer.description,
          category: offer.category,
          partnerId: offer.partner_id,
          address: offer.address,
          latitude: offer.latitude,
          longitude: offer.longitude,
          regularPrice: offer.regular_price,
          moneySaved: offer.money_saved,
          discountType: offer.discount_type,
          discountPercentage: offer.discount_percentage,
          discountAmount: offer.discount_amount,
          freeGoodieDescription: offer.free_goodie_description,
          redemptionMode: offer.redemption_mode,
          customRedemptionCode: offer.custom_redemption_code,
          redemptionType: offer.redemption_type,
          maxRedemptions: offer.max_redemptions,
          redemptionTimeframeDays: offer.redemption_timeframe_days,
          operatingHours: offer.operating_hours,
          imageUrl: offer.image_url,
          businessLogoUrl: offer.business_logo_url,
          barcodeUrl: freshBarcodeUrl, // Use fresh signed URL
          qrCode: offer.qr_code,
          expiresAt: offer.expires_at,
          status: offer.status,
          createdAt: offer.created_at,
          updatedAt: offer.updated_at,
          createdBy: offer.created_by
        };
      }));

      return offersWithFreshUrls;
    } catch (error) {
      console.error('Error fetching offers:', error);
      throw error;
    }
  }

  async deleteOfferPermanently(offerId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('offers')
        .delete()
        .eq('id', offerId);

      if (error) throw error;
    } catch (error) {
      console.error('Error permanently deleting offer:', error);
      throw error;
    }
  }

  async canUserRedeemOffer(offerId: string, userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .rpc('can_user_redeem_offer', {
          offer_uuid: offerId,
          user_uuid: userId
        });

      if (error) throw error;
      return data === true;
    } catch (error) {
      console.error('Error checking user redemption eligibility:', error);
      return false;
    }
  }

  async trackUserRedemption(offerId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .rpc('track_user_redemption', {
          offer_uuid: offerId,
          user_uuid: userId
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error tracking user redemption:', error);
      throw error;
    }
  }

  async getUserRedemptionHistory(userId: string, offerId?: string): Promise<any[]> {
    try {
      let query = supabase
        .from('user_offer_redemptions')
        .select(`
          *,
          offer:offers(
            title,
            discount_type,
            user_redemption_limit,
            user_redemption_reset_days
          )
        `)
        .eq('user_id', userId);

      if (offerId) {
        query = query.eq('offer_id', offerId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching user redemption history:', error);
      throw error;
    }
  }

  async createRedemption(offerData: any, userId: string): Promise<any> {
    try {
      // First check if user can redeem this offer
      const canRedeem = await this.canUserRedeemOffer(offerData.id, userId);
      if (!canRedeem) {
        throw new Error('You have reached the redemption limit for this offer. Please try again after the reset period.');
      }

      // Create the redemption
      const { data, error } = await supabase
        .from('redemptions')
        .insert([
          {
            offer_id: offerData.id,
            user_id: userId,
            redemption_code: offerData.qr_code,
            qr_code_used: 'MOBILE_APP_REDEMPTION'
          }
        ])
        .select()
        .single();

      if (error) throw error;

      // Track the user redemption for limit enforcement
      await this.trackUserRedemption(offerData.id, userId);

      return data;
    } catch (error) {
      console.error('Error creating redemption:', error);
      throw error;
    }
  }

  async uploadOfferImage(file: File, offerFolder: 'headers' | 'logos' | 'barcodes' = 'headers'): Promise<string> {
    // CRITICAL FIX: Completely public upload - no authentication checks
    // Safe because this function is only called from admin-only pages
    
    try {
      // REMOVED: Authentication check - making upload completely public
      // No checkPartnerAuth() call - upload is unrestricted
      
      // CRITICAL FIX: Use direct upload to offer-images bucket instead of temp_image_uploads workaround
      // This is the proper solution now that the bucket exists with correct RLS policies
      console.log('🚀 Uploading to offer-images bucket...');
      
      const result = await helperUploadOfferImage(file, null);
      
      if (!result || !result.url) {
        throw new Error('Upload failed - no URL returned');
      }
      
      console.log('✅ Upload successful - image stored in offer-images bucket');
      return result.url; // Returns public URL from offer-images bucket
    } catch (error: any) {
      console.error('❌ uploadOfferImage error:', error);
      throw error;
    }
  }

  /**
   * NEW: Helper to save image URLs to database after upload
   * Call this after successful upload to ensure URLs are persisted
   */
  async saveOfferImageUrls(offerId: string, imageUrls: {
    imageUrl?: string;
    businessLogoUrl?: string;
    barcodeUrl?: string;
  }): Promise<void> {
    const result = await updateOfferImageUrls(offerId, imageUrls);
    if (!result.success) {
      throw new Error(result.error || 'Failed to save image URLs');
    }
  }

  /**
   * NEW: Diagnose storage upload issues
   * Call this when uploads fail to get detailed diagnostic information
   */
  async diagnoseStorageIssue(): Promise<string> {
    return await diagnoseStorageIssue();
  }

  async uploadBarcode(file: File): Promise<string> {
    // CRITICAL FIX: Use new partner storage helper for barcode uploads
    
    try {
      // Check authentication first
      const authCheck = await checkPartnerAuth();
      if (!authCheck.isAuthenticated) {
        // Provide detailed diagnostics if auth fails
        const diagnostics = await diagnoseStorageIssue();
        console.error('❌ Authentication failed. Diagnostics:', diagnostics);
        throw new Error('You must be logged in to upload files. Please log in and try again.');
      }
      
      // Use helper function for upload
      const result = await helperUploadOfferBarcode(file);
      
      if (!result.success) {
        // Provide detailed error with diagnostics
        console.error('❌ Upload failed:', result.error, result.debugInfo);
        throw new Error(result.error || 'Upload failed');
      }
      
      if (!result.url) {
        throw new Error('Upload succeeded but no URL was returned');
      }
      
      return result.url;
    } catch (error: any) {
      console.error('❌ uploadBarcode error:', error);
      throw error;
    }
  }

  async getPartners(): Promise<Array<{ id: string; businessName: string; businessCategory: string }>> {
    const { data, error } = await supabase
      .from('partners')
      .select('id, business_name')
      .eq('status', 'active');

    if (error) throw error;

    return data?.map(partner => ({
      id: partner.id,
      businessName: partner.business_name,
      businessCategory: '' // Default empty value since column doesn't exist
    })) || [];
  }

  async saveDraft(offerData: Partial<CreateOfferData>): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Fallback to localStorage if not authenticated
        const draftKey = `offer-draft-${Date.now()}`;
        localStorage.setItem(draftKey, JSON.stringify(offerData));
        return true;
      }

      // Save to database if authenticated
      const { error } = await supabase
        .from('offer_drafts')
        .upsert({
          user_id: user.id,
          draft_data: offerData,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.warn('Failed to save draft to database, using localStorage:', error);
        // Fallback to localStorage
        const draftKey = `offer-draft-${user.id}`;
        localStorage.setItem(draftKey, JSON.stringify(offerData));
      }

      return true;
    } catch (error) {
      console.error('Error saving draft:', error);
      return false;
    }
  }

  async loadDraft(): Promise<Partial<CreateOfferData> | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Try to load from database first
        const { data, error } = await supabase
          .from('offer_drafts')
          .select('draft_data')
          .eq('user_id', user.id)
          .single();

        if (!error && data) {
          return data.draft_data;
        }

        // Fallback to localStorage for user-specific draft
        const userDraftKey = `offer-draft-${user.id}`;
        const userDraft = localStorage.getItem(userDraftKey);
        if (userDraft) {
          return JSON.parse(userDraft);
        }
      }

      // Fallback to general localStorage drafts
      const draftKeys = Object.keys(localStorage).filter(key => key.startsWith('offer-draft-'));
      if (draftKeys.length === 0) return null;
      
      // Get the most recent draft
      const latestKey = draftKeys.sort().pop();
      if (!latestKey) return null;
      
      const draftData = localStorage.getItem(latestKey);
      return draftData ? JSON.parse(draftData) : null;
    } catch (error) {
      console.error('Error loading draft:', error);
      return null;
    }
  }

  async deleteOffer(offerId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('offers')
        .update({ status: 'inactive' })
        .eq('id', offerId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting offer:', error);
      throw error;
    }
  }

  /**
   * Get offer by ID with admin override for edit access
   * CRITICAL: This ensures admins can ALWAYS fetch offers for editing
   */
  async getOfferById(offerId: string): Promise<Offer | null> {
    try {
      // CRITICAL FIX: Use imported supabase client, not this.supabase (which doesn't exist)
      const { data, error } = await supabase
        .from('offers')
        .select('*')
        .eq('id', offerId)
        .single();

      if (error) {
        console.error('Error fetching offer by ID:', error);
        // CRITICAL FIX: Provide more detailed error message for debugging
        throw new Error(`Failed to fetch offer: ${error.message}. This may be a permissions issue - please verify your admin role is set correctly.`);
      }

      if (!data) {
        return null;
      }

      // CRITICAL FIX: Transform database response to Offer interface
      // Regenerate signed URL for barcode if it exists
      let freshBarcodeUrl = data.barcode_url;
      
      if (data.barcode_url && (data.redemption_mode === 'barcode_upload' || data.redemption_mode === 'qr_upload')) {
        try {
          const urlObj = new URL(data.barcode_url);
          const pathMatch = urlObj.pathname.match(/\/storage\/v1\/object\/sign\/offer-barcodes\/(.+?)\?/);
          
          if (pathMatch && pathMatch[1]) {
            const storagePath = decodeURIComponent(pathMatch[1]);
            
            const { data: signedData, error: signError } = await supabase.storage
              .from('offer-barcodes')
              .createSignedUrl(storagePath, 3600 * 24 * 365);
            
            if (!signError && signedData) {
              freshBarcodeUrl = signedData.signedUrl;
            }
          }
        } catch (urlError) {
          console.warn('Failed to regenerate signed URL for barcode:', urlError);
        }
      }

      // FIXED: Map database offer_type to UI offerType
      let uiOfferType: 'restaurant' | 'attraction' = 'attraction';
      if (data.offer_type === 'gastronomy') {
        uiOfferType = 'restaurant';
      } else if (data.offer_type === 'standard') {
        uiOfferType = 'attraction';
      }

      return {
        id: data.id,
        offerId: data.offer_id,
        offerType: uiOfferType, // NEW: Use dedicated offer_type column
        title: data.title,
        description: data.description,
        category: data.category,
        partnerId: data.partner_id,
        address: data.address,
        latitude: data.latitude,
        longitude: data.longitude,
        regularPrice: data.regular_price,
        moneySaved: data.money_saved,
        discountType: data.discount_type,
        discountPercentage: data.discount_percentage,
        discountAmount: data.discount_amount,
        freeGoodieDescription: data.free_goodie_description,
        redemptionMode: data.redemption_mode,
        customRedemptionCode: data.custom_redemption_code,
        redemptionType: data.redemption_type,
        maxRedemptions: data.max_redemptions,
        redemptionTimeframeDays: data.redemption_timeframe_days,
        operatingHours: data.operating_hours,
        imageUrl: data.image_url,
        businessLogoUrl: data.business_logo_url,
        barcodeUrl: freshBarcodeUrl,
        qrCode: data.qr_code,
        expiresAt: data.expires_at,
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        createdBy: data.created_by
      };
    } catch (error: any) {
      console.error('Error in getOfferById:', error);
      throw error;
    }
  }

  async updateOffer(offerId: string, offerData: CreateOfferData): Promise<void> {
    try {
      // CRITICAL FIX: For partner authentication, we check if partnerId is provided
      // Partners use a custom localStorage-based auth system, not Supabase auth
      if (!offerData.partnerId) {
        // Only check Supabase auth if no partner ID is provided (admin updating offers)
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');
      }

      // CRITICAL FIX: Fetch existing offer first to preserve URL when needed
      const { data: existingOffer, error: fetchError } = await supabase
        .from('offers')
        .select('redemption_mode, redemption_url, custom_redemption_code')
        .eq('id', offerId)
        .single();

      if (fetchError) {
        console.error('Error fetching existing offer:', fetchError);
        throw new Error(`Failed to fetch existing offer: ${fetchError.message}`);
      }

      // CRITICAL FIX: Validate URL redemption mode requirements
      // If redemption mode is being updated to 'url', require URL
      if (offerData.redemptionMode === 'url') {
        if (!offerData.redemptionUrl || offerData.redemptionUrl.trim() === '') {
          throw new Error('URL is required when redemption mode is set to URL');
        }
        // Validate URL format
        try {
          new URL(offerData.redemptionUrl);
        } catch (urlError) {
          throw new Error('Invalid URL format. Please enter a valid URL starting with http:// or https://');
        }
      }

      // CRITICAL FIX: If existing offer has 'url' mode and no new URL provided, preserve existing
      if (existingOffer.redemption_mode === 'url' && !offerData.redemptionUrl && !offerData.redemptionMode) {
        // Preserve existing URL to avoid constraint violation
        offerData.redemptionUrl = existingOffer.redemption_url || existingOffer.custom_redemption_code;
      }

      // Get coordinates if address is being updated
      const coordinates = offerData.address ? 
        await this.getCoordinatesFromAddress(offerData.address) : null;

      const updateObject: any = {};
      
      // Only include fields that are provided
      if (offerData.title !== undefined) updateObject.title = offerData.title;
      if (offerData.description !== undefined) updateObject.description = offerData.description;
      if (offerData.category !== undefined) updateObject.category = offerData.category;
      if (offerData.partnerId !== undefined) updateObject.partner_id = offerData.partnerId;
      if (offerData.address !== undefined) updateObject.address = offerData.address;
      if (offerData.regularPrice !== undefined) updateObject.regular_price = offerData.regularPrice;
      if (offerData.discountType !== undefined) updateObject.discount_type = offerData.discountType;
      if (offerData.discountPercentage !== undefined) updateObject.discount_percentage = offerData.discountPercentage;
      if (offerData.discountAmount !== undefined) updateObject.discount_amount = offerData.discountAmount;
      if (offerData.freeGoodieDescription !== undefined) updateObject.free_goodie_description = offerData.freeGoodieDescription;
      if (offerData.redemptionMode !== undefined) {
        updateObject.redemption_mode = offerData.redemptionMode;
        
        // CRITICAL FIX: When redemption mode changes, handle URL redemption properly
        if (offerData.redemptionMode === 'custom_code') {
          // Keep custom code, clear barcode and URL
          updateObject.custom_redemption_code = offerData.customRedemptionCode || null;
          updateObject.barcode_url = null;
          updateObject.redemption_url = null; // Clear redemption_url when switching to custom_code
        } else if (offerData.redemptionMode === 'url') {
          // CRITICAL FIX: URL mode MUST have a valid URL - validation already done above
          updateObject.custom_redemption_code = offerData.redemptionUrl; // Store URL in custom code for backwards compatibility
          updateObject.redemption_url = offerData.redemptionUrl; // Set redemption_url field (REQUIRED by constraint)
          updateObject.barcode_url = null;
        } else if (offerData.redemptionMode === 'barcode_upload' || offerData.redemptionMode === 'qr_upload') {
          // Keep barcode, clear custom code and URL
          updateObject.barcode_url = offerData.barcodeUrl || null;
          updateObject.custom_redemption_code = null;
          updateObject.redemption_url = null; // Clear redemption_url when switching to barcode/QR
        } else if (offerData.redemptionMode === 'auto_generate') {
          // Auto-generate mode: clear all manual redemption fields
          updateObject.custom_redemption_code = null;
          updateObject.barcode_url = null;
          updateObject.redemption_url = null;
        } else {
          // Clear all for other modes
          updateObject.custom_redemption_code = null;
          updateObject.barcode_url = null;
          updateObject.redemption_url = null;
        }
      } else {
        // CRITICAL FIX: If redemption mode is NOT being updated
        // Check if we need to preserve the existing URL
        if (existingOffer.redemption_mode === 'url') {
          // Preserve redemption_url to satisfy database constraint
          if (offerData.redemptionUrl !== undefined) {
            // URL is being explicitly updated
            if (offerData.redemptionUrl && offerData.redemptionUrl.trim() !== '') {
              try {
                new URL(offerData.redemptionUrl);
                updateObject.redemption_url = offerData.redemptionUrl;
              } catch (urlError) {
                throw new Error('Invalid URL format. Please enter a valid URL starting with http:// or https://');
              }
            } else {
              updateObject.redemption_url = null;
            }
          } else {
            // URL not provided, preserve existing to avoid constraint violation
            updateObject.redemption_url = existingOffer.redemption_url || existingOffer.custom_redemption_code;
          }
        } else {
          // For non-URL modes, handle individual fields if provided
          if (offerData.customRedemptionCode !== undefined) updateObject.custom_redemption_code = offerData.customRedemptionCode;
          if (offerData.barcodeUrl !== undefined) updateObject.barcode_url = offerData.barcodeUrl;
          if (offerData.redemptionUrl !== undefined) {
            if (offerData.redemptionUrl && offerData.redemptionUrl.trim() !== '') {
              try {
                new URL(offerData.redemptionUrl);
                updateObject.redemption_url = offerData.redemptionUrl;
              } catch (urlError) {
                throw new Error('Invalid URL format. Please enter a valid URL starting with http:// or https://');
              }
            } else {
              updateObject.redemption_url = null;
            }
          }
        }
      }
      if (offerData.redemptionType !== undefined) updateObject.redemption_type = offerData.redemptionType;
      if (offerData.maxRedemptions !== undefined) updateObject.max_redemptions = offerData.maxRedemptions;
      if (offerData.userRedemptionLimit !== undefined) updateObject.user_redemption_limit = offerData.userRedemptionLimit;
      if (offerData.userRedemptionResetDays !== undefined) updateObject.user_redemption_reset_days = offerData.userRedemptionResetDays;
      if (offerData.operatingHours !== undefined) updateObject.operating_hours = offerData.operatingHours;
      if (offerData.imageUrl !== undefined) updateObject.image_url = offerData.imageUrl;
      if (offerData.businessLogoUrl !== undefined) updateObject.business_logo_url = offerData.businessLogoUrl;
      if (offerData.expiresAt !== undefined) updateObject.expires_at = offerData.expiresAt;
      if (offerData.status !== undefined) updateObject.status = offerData.status;
      if (offerData.businessName !== undefined) updateObject.business_name = offerData.businessName;

      // FIXED: Update both offer_type and standard_category when offerType changes
      if (offerData.offerType !== undefined) {
        const dbOfferType = offerData.offerType === 'restaurant' || offerData.offerType === 'gastronomy' ? 'gastronomy' : 'standard';
        updateObject.offer_type = dbOfferType; // NEW: Update dedicated offer_type column
        updateObject.standard_category = dbOfferType === 'gastronomy' ? 'Restaurants & Food' : 'Attractions'; // Keep for backwards compatibility
      }

      // Add coordinates if address was updated
      if (coordinates) {
        updateObject.latitude = coordinates.latitude;
        updateObject.longitude = coordinates.longitude;
      }

      // Always update the updated_at timestamp
      updateObject.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from('offers')
        .update({
          title: updateObject.title,
          description: updateObject.description,
          category: updateObject.category,
          offer_type: updateObject.offer_type,
          standard_category: updateObject.standard_category,
          partner_id: updateObject.partner_id,
          address: updateObject.address,
          regular_price: updateObject.regular_price,
          discount_type: updateObject.discount_type,
          discount_percentage: updateObject.discount_percentage,
          discount_amount: updateObject.discount_amount,
          free_goodie_description: updateObject.free_goodie_description,
          redemption_mode: updateObject.redemption_mode,
          custom_redemption_code: updateObject.custom_redemption_code,
          redemption_url: updateObject.redemption_url,
          redemption_type: updateObject.redemption_type,
          max_redemptions: updateObject.max_redemptions,
          redemption_timeframe_days: updateObject.redemption_timeframe_days,
          user_redemption_limit: updateObject.user_redemption_limit,
          user_redemption_reset_days: updateObject.user_redemption_reset_days,
          operating_hours: updateObject.operating_hours,
          image_url: updateObject.image_url,
          business_logo_url: updateObject.business_logo_url,
          barcode_url: updateObject.barcode_url,
          qr_code: updateObject.qr_code,
          expires_at: updateObject.expires_at,
          latitude: updateObject.latitude,
          longitude: updateObject.longitude,
          business_name: updateObject.business_name,
          is_featured: offerData.isFeatured !== undefined ? offerData.isFeatured : undefined, // NEW: Update featured status if provided
        })
        .eq('id', offerId);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating offer:', error);
      throw error;
    }
  }

  async bulkUpdateOfferStatus(offerIds: string[], newStatus: 'active' | 'inactive'): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('offers')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .in('id', offerIds);

      if (error) throw error;
    } catch (error) {
      console.error('Error bulk updating offer status:', error);
      throw error;
    }
  }

  async bulkDeleteOffers(offerIds: string[]): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('offers')
        .delete()
        .in('id', offerIds);

      if (error) throw error;
    } catch (error) {
      console.error('Error bulk deleting offers:', error);
      throw error;
    }
  }

  async getRedemptionsPerDay(startDate: Date, endDate: Date): Promise<Array<{ date: string; redemptions: number; revenue: number }>> {
    try {
      // Get redemption data from the actual redemptions table
      const { data: redemptionData, error } = await supabase
        .from('redemptions')
        .select('redemption_date, saved_amount, final_price')
        .gte('redemption_date', startDate.toISOString())
        .lte('redemption_date', endDate.toISOString())
        .order('redemption_date', { ascending: true });

      if (error) throw error;

      // Process data by date
      const dateMap = new Map<string, { redemptions: number; revenue: number }>();
      
      // Initialize dates with zeros
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        dateMap.set(dateStr, { redemptions: 0, revenue: 0 });
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Aggregate real redemption data
      redemptionData?.forEach(item => {
        const dateStr = new Date(item.redemption_date).toISOString().split('T')[0];
        const existing = dateMap.get(dateStr);
        if (existing) {
          existing.redemptions++;
          existing.revenue += item.final_price || 0;
        }
      });

      return Array.from(dateMap.entries()).map(([date, data]) => ({
        date,
        redemptions: data.redemptions,
        revenue: data.revenue
      }));
    } catch (error) {
      console.error('Error fetching redemptions per day:', error);
      return [];
    }
  }

  async getPerformanceAnalytics(startDate: Date, endDate: Date): Promise<Array<{
    date: string;
    redemptions: number;
    revenue: number;
    userEngagementScore: number;
    averageSavingsPerUser: number;
  }>> {
    try {
      // Get comprehensive performance data combining redemptions, offers, and user analytics
      const { data: redemptionData, error: redemptionError } = await supabase
        .from('redemptions')
        .select('redemption_date, saved_amount, final_price, user_id')
        .gte('redemption_date', startDate.toISOString())
        .lte('redemption_date', endDate.toISOString())
        .order('redemption_date', { ascending: true });

      if (redemptionError) throw redemptionError;

      // Get offers created in the same period for engagement calculation
      const { data: offersData, error: offersError } = await supabase
        .from('offers')
        .select('created_at, status')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (offersError) throw offersError;

      // Get user analytics events for engagement scoring
      const { data: analyticsData, error: analyticsError } = await supabase
        .from('analytics_events')
        .select('created_at, event_type, user_id')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (analyticsError) throw analyticsError;

      // Process data by date
      const dateMap = new Map<string, {
        redemptions: number;
        revenue: number;
        uniqueUsers: Set<string>;
        totalSavings: number;
        offersCreated: number;
        userInteractions: number;
      }>();
      
      // Initialize dates with empty data structures
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        dateMap.set(dateStr, {
          redemptions: 0,
          revenue: 0,
          uniqueUsers: new Set(),
          totalSavings: 0,
          offersCreated: 0,
          userInteractions: 0
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Aggregate redemption data
      redemptionData?.forEach(item => {
        const dateStr = new Date(item.redemption_date).toISOString().split('T')[0];
        const existing = dateMap.get(dateStr);
        if (existing) {
          existing.redemptions++;
          existing.revenue += item.final_price || 0;
          existing.totalSavings += item.saved_amount || 0;
          if (item.user_id) {
            existing.uniqueUsers.add(item.user_id);
          }
        }
      });

      // Aggregate offers data for engagement calculation
      offersData?.forEach(offer => {
        const dateStr = new Date(offer.created_at).toISOString().split('T')[0];
        const existing = dateMap.get(dateStr);
        if (existing) {
          existing.offersCreated++;
        }
      });

      // Aggregate analytics events for user interaction scoring
      analyticsData?.forEach(event => {
        const dateStr = new Date(event.created_at).toISOString().split('T')[0];
        const existing = dateMap.get(dateStr);
        if (existing) {
          existing.userInteractions++;
          if (event.user_id) {
            existing.uniqueUsers.add(event.user_id);
          }
        }
      });

      return Array.from(dateMap.entries()).map(([date, data]) => {
        // Calculate User Engagement Score: (redemptions + interactions) / (offers created + base activity) * 100
        const baseActivity = Math.max(data.offersCreated, 1); // Ensure we don't divide by zero
        const totalActivity = data.redemptions + data.userInteractions;
        const userEngagementScore = Math.min((totalActivity / baseActivity) * 20, 100); // Scale to 0-100%

        // Calculate Average Savings Per User
        const uniqueUserCount = data.uniqueUsers.size || 1; // Avoid division by zero
        const averageSavingsPerUser = data.totalSavings / uniqueUserCount;

        return {
          date,
          redemptions: data.redemptions,
          revenue: data.revenue,
          userEngagementScore: Math.round(userEngagementScore * 10) / 10, // Round to 1 decimal
          averageSavingsPerUser: Math.round(averageSavingsPerUser * 100) / 100 // Round to 2 decimals
        };
      });
    } catch (error) {
      console.error('Error fetching performance analytics:', error);
      // Return fallback data structure
      const fallbackData = [];
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        fallbackData.push({
          date: dateStr,
          redemptions: 0,
          revenue: 0,
          userEngagementScore: 0,
          averageSavingsPerUser: 0
        });
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return fallbackData;
    }
  }
}

export const offerService = new OfferService();

export const createPartnerOffer = async (offerData: PartnerOfferFormData) => {
  try {
    const { data: partnerData } = await supabase.auth.getUser();
    if (!partnerData.user) throw new Error('No authenticated user found');

    // Get partner details from user profile
    const { data: userProfile, error: profileError } = await supabase
      .from('user_profiles')
      .select('partner_id')
      .eq('id', partnerData.user.id)
      .single();

    if (profileError || !userProfile?.partner_id) {
      throw new Error('Partner profile not found');
    }

    // Map UI offerType to database offer_type values
    const dbOfferType = offerData.offerType === 'gastronomy' ? 'gastronomy' : 'standard';

    // Call the database function with explicit offer_type parameter
    const { data, error } = await supabase.rpc('create_partner_offer', {
      p_partner_id: userProfile.partner_id,
      p_title: offerData.title,
      p_description: offerData.description,
      p_category: offerData.category,
      p_offer_type: dbOfferType, // NEW: Pass explicit offer_type instead of p_standard_category
      p_address: offerData.address || null,
      p_regular_price: offerData.regularPrice || null,
      p_discount_type: offerData.discountType || 'percentage',
      p_discount_percentage: offerData.discountPercentage || null,
      p_discount_amount: offerData.discountAmount || null,
      p_free_goodie_description: offerData.freeGoodieDescription || null,
      p_redemption_mode: offerData.redemptionMode || 'qr_upload',
      p_custom_redemption_code: offerData.redemptionMode === 'custom_code' ? offerData.customRedemptionCode : null,
      p_redemption_type: offerData.redemptionType || 'unlimited',
      p_max_redemptions: offerData.maxRedemptions || null,
      p_user_redemption_limit: offerData.userRedemptionLimit || null,
      p_user_redemption_reset_days: offerData.userRedemptionResetDays || null,
      p_operating_hours: offerData.operatingHours || null,
      p_image_url: offerData.imageUrl || null,
      p_business_logo_url: offerData.businessLogoUrl || null,
      p_barcode_url: offerData.barcodeUrl || null,
      p_qr_code: offerData.qrCode || null,
      p_expires_at: offerData.expiresAt || null,
      p_latitude: offerData.latitude || null,
      p_longitude: offerData.longitude || null,
      p_business_name: offerData.businessName || 'Local Business'
    });

    if (error) {
      console.error('Error creating partner offer:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      throw new Error('No response from create_partner_offer function');
    }

    const result = data[0];

    if (!result.success) {
      throw new Error(result.message || 'Failed to create offer');
    }

    return {
      success: true,
      message: result.message,
      offerId: result.offer_id
    };
  } catch (error) {
    console.error('Error in createPartnerOffer:', error);
    throw error;
  }
};